import React from 'react';
import * as PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { amplitudeLogEvent } from '../../helpers/amplitude-helper';
import { connect } from '../../connect';
import { CompactItemList } from '../../components/compact-item-list';
import { LoadingIndicator } from '../../components/loading-indicator';
import { NotFound } from '../../components/not-found';
import { getCategory, getCategoryByToken, dynamicCategories } from '../../actions';
import logger from '../../utils/logger';
import './index.less';
import { IAuthState } from '../../reducers/auth-reducers';
import { ISubscriptionState } from '../../reducers/subscription-reducers';
import { IKidsModeState } from '../../reducers/kids-mode-reducers';
import { ICategoryState } from '../../reducers/category-reducers';
import { IReduxProps } from '../../actions/action-types';
import { ICategoriesState } from '../../reducers/home-reducers';
import { createOpenGraphMetadata } from '../../helpers/metadata-helper';
import baseUrls from '../../data/baseUrls';

function mapStateToProps(
	{ category, auth, subscription, kidsMode, categories },
	{
		match: {
			params: { categoryId, categoryToken },
		},
	}
) {
	return { category, auth, subscription, kidsMode, categoryId, categoryToken, categories };
}

interface ICategoryPageProps extends IReduxProps {
	auth: IAuthState;
	category: ICategoryState;
	categoryId?: string;
	categoryToken?: string;
	subscription: ISubscriptionState;
	kidsMode: IKidsModeState;
	categories: ICategoriesState;
}

@connect(mapStateToProps)
export default class CategoryPage extends React.Component<ICategoryPageProps> {
	static contextTypes = {
		router: PropTypes.object.isRequired,
	};

	static async activate(store, { params: { categoryId, categoryToken }, staticContext }) {
		await store
			.dispatch(categoryToken ? getCategoryByToken(categoryToken) : getCategory(categoryId))
			.catch(logger.error);

		if (staticContext) {
			const {
				category: { category, error },
			} = store.getState();
			const statusCode = error?.statusCode;
			if (statusCode === 404) {
				staticContext.statusCode = 404;
			} else if (statusCode) {
				staticContext.statusCode = 500;
			} else if (!category) {
				staticContext.statusCode = 404;
			}
		}
	}

	componentDidMount() {
		const {
			categories: { categories },
			categoryToken,
			categoryId,
			category,
			kidsMode: { isKidsMode },
			dispatch,
		} = this.props;
		amplitudeLogEvent('Category - Viewed', {
			Token: categoryToken,
		});
		const isDifferentCategory =
			!category.category ||
			(categoryToken && categoryToken !== category.category.token) ||
			(categoryId && categoryId !== category.category.id);
		const isDynamicCategory = dynamicCategories.some(x => x.id === categoryId || x.token === categoryToken);
		const isRecommendedCategory =
			categoryId &&
			categories.categories &&
			categories.categories.some(
				c => c.subcategories && c.subcategories.some(sc => sc.id === categoryId && sc.token === 'recommended')
			);

		if (
			(isKidsMode && isDifferentCategory) ||
			(!isKidsMode && (isDynamicCategory || isDifferentCategory || isRecommendedCategory))
		) {
			dispatch(
				categoryToken ? getCategoryByToken(categoryToken, true, isKidsMode) : getCategory(categoryId, true, isKidsMode)
			).catch(logger.error);
		}
	}

	render() {
		const {
			auth,
			subscription,
			category: { isFetching, category },
		} = this.props;
		const headLinks =
			category && category.token
				? [
						{
							rel: 'canonical',
							href: `${baseUrls.faithlifeTv}/categories/${encodeURIComponent(category.token)}`,
						},
				  ]
				: [];
		const meta = createOpenGraphMetadata();

		return (
			<div className="category-page">
				<Helmet title={category && category.title ? category.title : ''} meta={meta} link={headLinks} />
				{auth.isProcessing || isFetching ? (
					<LoadingIndicator />
				) : !category ? (
					<NotFound />
				) : (
					<div className="content">
						<h3>{category.title}</h3>
						<CompactItemList
							items={category.items ? category.items : []}
							imageHeight={170}
							subscription={subscription}
						/>
					</div>
				)}
			</div>
		);
	}
}
